import {
  MediaRecorderAudioChunk,
  MediaRecorderMimeType,
} from "@/store/audio/recording/media-types";
import {
  DESKTOP_PCM_MIME_PREFIX,
  desktopPcmMime,
} from "@/modules/audio-recording/capture/desktop-pcm-persistence";
import { createDetachedBlobFromBufferView } from "@/modules/audio-recording/blob-utils";

export function buildRecorderChunkFromPcm({
  samples,
  sampleRate,
  mimeType = MediaRecorderMimeType.Pcm16,
}: {
  samples: Int16Array;
  sampleRate: number;
  mimeType?: MediaRecorderMimeType;
}): MediaRecorderAudioChunk {
  const durationMs = Math.round((samples.length / sampleRate) * 1000);
  const blob = createDetachedBlobFromBufferView(samples, { type: mimeType });

  return {
    blob,
    durationMs,
    mimeType,
    byteLength: blob.size,
  };
}

const isPcmMimeType = (value?: string | null): boolean => {
  if (!value) return false;
  const lower = value.toLowerCase();
  return lower.startsWith(DESKTOP_PCM_MIME_PREFIX) || lower.startsWith("audio/pcm");
};

export async function transformBlobToPlayableFormat({
  blobs,
}: {
  blobs: Blob[];
}): Promise<Blob | null> {
  const validBlobs = blobs.filter((blob): blob is Blob => blob instanceof Blob);
  if (!validBlobs.length) return null;

  const allPcm = validBlobs.every(blob => isPcmMimeType(blob.type));

  if (allPcm) {
    return null;
  }

  const mimeType = validBlobs[0]?.type || MediaRecorderMimeType.WebmOpus;
  return new Blob(validBlobs, { type: mimeType });
}

export async function transformBlobToUploadFormat({
  blobs,
}: {
  blobs: Blob[];
}): Promise<Blob | null> {
  const validBlobs = blobs.filter((blob): blob is Blob => blob instanceof Blob);
  if (!validBlobs.length) return null;

  const allPcm = validBlobs.every(blob => isPcmMimeType(blob.type));
  if (allPcm) {
    const preferredMimeType =
      validBlobs.find(blob => blob.type?.toLowerCase().startsWith(DESKTOP_PCM_MIME_PREFIX))?.type ??
      desktopPcmMime();
    return new Blob(validBlobs, { type: preferredMimeType });
  }

  const mimeType = validBlobs[0]?.type || MediaRecorderMimeType.WebmOpus;
  return new Blob(validBlobs, { type: mimeType });
}
